import { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import SoldxHeader from "../../../components/SoldxHeader"
import AuctionCard, { ApiListing } from "../../../components/AuctionCard"
import apiClient from "helpers/apiClient"
import { navigateToPropertyDetail, type PropertyKycBlockReason } from "helpers/auth_api"
import KycRequiredModal from "../../../components/KycRequiredModal/KycRequiredModal"
import SoldxLandingFooter from "../../../components/SoldxLandingFooter"

const SEARCH_LISTINGS_URL = "/api/listings/search"

const heroCategories = [
  { key: "Commercial Office Space", label: "Commercial Office", icon: "mdi-domain" },
  { key: "Retail Space", label: "Retail Space", icon: "mdi-shopping" },
  { key: "Warehouse", label: "Warehouse", icon: "mdi-warehouse" },
  { key: "Hospitality", label: "Hospitality", icon: "mdi-bed" },
  { key: "Franchise", label: "Franchise", icon: "mdi-storefront" },
  { key: "Land", label: "Land", icon: "mdi-terrain" },
  { key: "Independent Building", label: "Independent Building", icon: "mdi-office-building" },
]


const teamCards = [
  {
    title: "For Brokers",
    image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop&q=60",
    description: "Partner with India's leading auction platform. Get access to exclusive listings and close deals faster with transparent, digital auctions.",
    buttonText: "Create a listing",
    buttonLink: "/broker",
  },
  {
    title: "For Buyers",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=300&h=200&fit=crop&q=60",
    description: "Discover commercial and residential properties at competitive prices. Bid in real-time and secure your dream asset with full transparency.",
    buttonText: "Find your dream home",
    buttonLink: "/properties",
  },
  {
    title: "For Seller",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=300&h=200&fit=crop&q=60",
    description: "Sell faster with our auction model. Reach verified buyers, get fair market value, and complete transactions with minimal hassle.",
    buttonText: "Learn More",
    buttonLink: "/seller",
  },
]

const exploreAuctionCategories = [
  {
    id: "commercial",
    title: "Commercial assets",
    description: "Planning to buy office space, shopping malls, or commercial land.",
    exploreTo: "/properties?category=Commercial%20Office%20Space",
    images: [
      "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=800&fit=crop&q=80",
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=800&fit=crop&q=80",
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&h=800&fit=crop&q=80",
    ],
  },
  {
    id: "industrial",
    title: "Industrial assets",
    description: "Planning to buy industrial land, godowns, or sheds with plant and machinery.",
    exploreTo: "/properties?category=Independent%20Building%20(Industrial)",
    images: [
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=800&fit=crop&q=80",
      "https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=1200&h=800&fit=crop&q=80",
      "https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=1200&h=800&fit=crop&q=80",
    ],
  },
]

const Home = () => {
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState("")
  const [location, setLocation] = useState("")
  const [liveListings, setLiveListings] = useState<ApiListing[]>([])
  const [liveLoading, setLiveLoading] = useState(true)
  const [kycModalOpen, setKycModalOpen] = useState(false)
  const [kycModalVariant, setKycModalVariant] = useState<PropertyKycBlockReason>("upload")
  const [exploreActiveIndex, setExploreActiveIndex] = useState(0)
  const [exploreSlide, setExploreSlide] = useState(0)
  const categoryScrollRef = useRef<HTMLDivElement>(null)

  const exploreCategory = exploreAuctionCategories[exploreActiveIndex]
  const exploreImages = exploreCategory?.images ?? []
  const exploreSlideCount = exploreImages.length

  useEffect(() => {
    setExploreSlide(0)
  }, [exploreActiveIndex])

  useEffect(() => {
    setLiveLoading(true)
    apiClient
      .get<{ success: boolean; data?: ApiListing[] }>(SEARCH_LISTINGS_URL, { params: { limit: 20, page: 1 } })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const liveOnly = res.data.data.filter((l) => l.auctionStatus === "live")
          setLiveListings(liveOnly)
        } else {
          setLiveListings([])
        }
      })
      .catch(() => setLiveListings([]))
      .finally(() => setLiveLoading(false))
  }, [])

  const handleHeroSearch = () => {
    const params = new URLSearchParams()
    if (location.trim()) params.set("location", location.trim())
    if (selectedCategory) params.set("category", selectedCategory)
    navigate(`/properties${params.toString() ? `?${params.toString()}` : ""}`)
  }

  return (
    <div className="soldx-landing soldx-home-page">
      <SoldxHeader solid />

      {/* Hero Section - Commercial Marketplace Style */}
      <section className="hero-marketplace">
        <div className="hero-marketplace-bg">
          {/* <img
            src="https://res.cloudinary.com/dkzvtdxie/image/upload/v1773508436/landscape-modern-city_1_qs3m0i.webp"
            alt="City skyline"
          /> */}
          <img
            src="https://res.cloudinary.com/dkzvtdxie/image/upload/v1780908827/soldxbanner_qvdd7h.png"
            alt="City skyline"
          />
          <div className="hero-marketplace-overlay" />
        </div>
        <div className="hero-marketplace-content">
          <h1 className="hero-marketplace-title">
            India's #1 Commercial Real Estate Marketplace
          </h1>

          <div className="hero-search-card">
            <div className="hero-tabs hero-tabs-single">
              <span className="hero-tab-label">Auctions</span>
            </div>

            {/* Category Icons */}
            <div className="hero-categories-wrapper">

              <div className="hero-categories" ref={categoryScrollRef}>
                {heroCategories.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    className={`hero-category-item ${selectedCategory === cat.key ? "active" : ""}`}
                    onClick={() => {
                      setSelectedCategory(cat.key)
                      navigate(`/properties?category=${encodeURIComponent(cat.key)}`)
                    }}
                    aria-label={`View ${cat.label} properties`}
                  >
                    <i className={`mdi ${cat.icon}`} />
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>

            </div>

            {/* Search Input */}
            <div className="hero-search-input-wrapper">
              <div className="hero-search-input">
                <i className="mdi mdi-map-marker-outline" />
                <input
                  type="text"
                  placeholder="Enter a location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleHeroSearch()}
                />
              </div>
              <button className="hero-search-btn" onClick={handleHeroSearch}>
                <i className="mdi mdi-magnify" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Explore auction properties — tab + carousel (reference layout) */}


      {/* Live Auctions - only live listings from API */}
      <section className="soldx-section soldx-featured">
        <div className="container">
          <div className="soldx-featured-header">
            <div className="soldx-featured-heading-wrap">
              <h2 className="soldx-featured-title">
                Live <span className="soldx-featured-title-accent">Auctions</span>
              </h2>
              <p className="soldx-featured-desc">
                Bid in real time on commercial  properties as they go under the hammer.
              </p>
            </div>
            <div className="soldx-featured-actions">
              <Link to="/live-auction" className="soldx-featured-view-btn">
                View listings
                <i className="mdi mdi-arrow-top-right" />
              </Link>
            </div>
          </div>
          {liveLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-2 text-muted small">Loading live auctions...</p>
            </div>
          )}
          {!liveLoading && liveListings.length === 0 && (
            <div className="text-center py-5 text-muted">
              <p className="mb-2">No live auctions right now.</p>
              <Link to="/properties">Explore upcoming properties</Link>
            </div>
          )}
          {!liveLoading && liveListings.length > 0 && (
            <div className="row g-4">
              {liveListings.map((listing) => (
                <div key={listing._id} className="col-md-6 col-lg-4">
                  <AuctionCard
                    listing={listing}
                    onViewDetails={(l) =>
                      navigateToPropertyDetail(navigate, l._id, {
                        onKycRequired: (reason) => {
                          setKycModalVariant(reason)
                          setKycModalOpen(true)
                        },
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="soldx-explore-auction" aria-labelledby="soldx-explore-auction-heading">
        <div className="container soldx-explore-auction-inner">
          <div className="row g-4 g-lg-5 align-items-stretch">
            <div className="col-lg-5">
              <h2 id="soldx-explore-auction-heading" className="soldx-explore-auction-title">
                Explore auction properties
              </h2>
              <p className="soldx-explore-auction-intro">
                Looking for a property you have always dreamed of? Experience a seamless choice of properties and
                stellar support by our experts at SoldX.
              </p>
              <div className="soldx-explore-auction-divider" role="presentation" />
              <div className="soldx-explore-auction-tabs" role="tablist" aria-label="Property categories">
                {exploreAuctionCategories.map((cat, idx) => (
                  <div
                    key={cat.id}
                    role="tab"
                    tabIndex={exploreActiveIndex === idx ? 0 : -1}
                    id={`explore-tab-${cat.id}`}
                    aria-selected={exploreActiveIndex === idx}
                    aria-controls="explore-carousel-panel"
                    className={`soldx-explore-auction-tab ${exploreActiveIndex === idx ? "is-active" : ""}`}
                    onClick={() => setExploreActiveIndex(idx)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        setExploreActiveIndex(idx)
                      }
                    }}
                  >
                    <span className="soldx-explore-auction-tab-title">{cat.title}</span>
                    <span className="soldx-explore-auction-tab-desc">{cat.description}</span>
                    <Link to={cat.exploreTo} className="soldx-explore-auction-tab-link" onClick={(e) => e.stopPropagation()}>
                      Explore <i className="mdi mdi-chevron-right" aria-hidden />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-7">
              <div
                className="soldx-explore-auction-carousel"
                id="explore-carousel-panel"
                role="tabpanel"
                aria-labelledby={`explore-tab-${exploreCategory?.id}`}
              >
                <div className="soldx-explore-auction-carousel-frame">
                  {exploreImages.map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt={`${exploreCategory.title} — slide ${i + 1}`}
                      className={`soldx-explore-auction-carousel-img ${i === exploreSlide ? "is-visible" : ""}`}
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  ))}
                </div>
                <div className="soldx-explore-auction-carousel-controls">
                  <button
                    type="button"
                    className="soldx-explore-auction-nav"
                    aria-label="Previous image"
                    onClick={() =>
                      setExploreSlide((s) => (s - 1 + exploreSlideCount) % Math.max(exploreSlideCount, 1))
                    }
                  >
                    <i className="mdi mdi-chevron-left" aria-hidden />
                  </button>
                  <div className="soldx-explore-auction-dots" role="tablist" aria-label="Carousel slides">
                    {exploreImages.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`soldx-explore-auction-dot ${i === exploreSlide ? "is-active" : ""}`}
                        aria-label={`Slide ${i + 1}`}
                        aria-current={i === exploreSlide ? "true" : undefined}
                        onClick={() => setExploreSlide(i)}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="soldx-explore-auction-nav"
                    aria-label="Next image"
                    onClick={() => setExploreSlide((s) => (s + 1) % Math.max(exploreSlideCount, 1))}
                  >
                    <i className="mdi mdi-chevron-right" aria-hidden />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rely On Our Team */}
      <section className="soldx-section soldx-team">
        <div className="container-max">
          <h2 className="soldx-section-title text-center mb-5">RELY ON OUR TEAM</h2>
          <div className="row g-4">
            {teamCards.map((card) => (
              <div key={card.title} className="col-md-6 col-lg-4">
                <div className="soldx-team-card">
                  <div className="soldx-team-card-image">
                    <img src={card.image} alt={card.title} loading="lazy" />
                  </div>
                  <div className="soldx-team-card-body">
                    <h3 className="soldx-team-card-title">{card.title}</h3>
                    <p className="soldx-team-card-text">{card.description}</p>
                    <Link to={card.buttonLink} className="btn btn-primary soldx-team-card-btn">
                      {card.buttonText}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="soldx-cta">
        <div className="soldx-cta-bg" />
        <div className="soldx-cta-content">
          <h2 className="soldx-cta-title">
            Why just sell it when you can <span className="soldx-cta-brand">SoldX it.</span>
          </h2>
          <div className="soldx-cta-buttons">
            <Link to="/properties" className="btn soldx-cta-btn soldx-cta-btn-primary">
              Start Bidding
            </Link>
            <button type="button" className="btn soldx-cta-btn soldx-cta-btn-danger">
              Get Property Alerts
            </button>
          </div>
        </div>
      </section>

      <SoldxLandingFooter />
      <KycRequiredModal isOpen={kycModalOpen} variant={kycModalVariant} toggle={() => setKycModalOpen(false)} />
    </div>
  )
}

export default Home
